// Shared Apryse WebViewer bootstrap. Both ApryseContractRenderer (contracts)
// and PresentationStageRenderer (presentations) call into this so the
// constructor options, license source, and document loading stay aligned.

const SUPPORTED_EXTS = [
  'pdf',
  'docx', 'doc',
  'xlsx', 'xls',
  'pptx', 'ppt',
  'png', 'jpg', 'jpeg', 'gif', 'bmp',
];

export function sanitizeUrl(url) {
  if (!url) return url;
  if (url.startsWith('http://')) return url.replace('http://', 'https://');
  return url;
}

export function detectExtension(rawUrl, fileTypeHint) {
  const cleanPath = (rawUrl || '').split('?')[0].split('#')[0];
  const extFromUrl = cleanPath.split('.').pop()?.toLowerCase() || '';
  if (SUPPORTED_EXTS.includes(extFromUrl)) return extFromUrl;
  if (fileTypeHint) {
    const hint = String(fileTypeHint).toLowerCase();
    if (SUPPORTED_EXTS.includes(hint)) return hint;
    if (hint.includes('pdf')) return 'pdf';
    if (hint.includes('word') || hint.includes('docx')) return 'docx';
    if (hint.includes('sheet') || hint.includes('excel')) return 'xlsx';
    if (hint.includes('presentation') || hint.includes('powerpoint')) return 'pptx';
    // 'image' is the renderer's generic type — pick the actual ext from the URL
    // if possible, otherwise default to png so Apryse loads it as an image.
    if (hint === 'image') return SUPPORTED_EXTS.includes(extFromUrl) ? extFromUrl : 'png';
  }
  return 'pdf';
}

// S3 doesn't send CORS headers, so route documents through the backend proxy.
export function buildProxiedUrl(rawUrl) {
  const proxyBase = (process.env.REACT_APP_BASE_URL || '').replace(/\/$/, '');
  return `${proxyBase}/proxy/resource?url=${encodeURIComponent(sanitizeUrl(rawUrl))}`;
}

export async function createApryseWebViewer(container, {
  fileUrl,
  fileType,
  filename,
  enableAnnotations = false,
  disabledElements = [],
}) {
  const mod = await import('@pdftron/webviewer');
  const WebViewer = mod.default;

  const instance = await WebViewer(
    {
      path: '/lib/webviewer',
      licenseKey: process.env.REACT_APP_APRYSE_LICENSE,
      enableAnnotations,
      enableFilePicker: false,
      disableVirtualDisplayMode: true,
      fullAPI: true,
      disabledElements,
    },
    container
  );

  const rawUrl = sanitizeUrl(fileUrl);
  const extension = detectExtension(rawUrl, fileType);
  const proxiedUrl = buildProxiedUrl(rawUrl);

  instance.UI.loadDocument(proxiedUrl, {
    extension,
    filename: filename || `document.${extension}`,
  });

  return { instance, extension, rawUrl, proxiedUrl };
}
